import React, { useEffect, useState } from 'react';
import { Button, Card, Empty, Form, Input, Modal, Select, Space, Table } from 'antd';
import { PlusOutlined } from '@ant-design/icons';
import { useDispatch } from 'react-redux';
import { createSelector } from 'reselect';
import { useSelector } from 'react-redux';
import _ from 'lodash';

import usecustomStyles from '../Common/customStyles';
import Spinners from '../Common/Spinner';
import { DeleteModal } from '../Common/DeleteModal';
import '../assets/scss/_custom.scss';
import { USER_ROLE_VALUE_ENUM } from '../Libs/value_enum';
import { themecolor } from '../config';
import * as PThunk from '../slices/project/thunk';
import * as SThunk from '../slices/sites/thunk';
import * as CThunk from '../slices/company/thunk';
import * as HPermission from '../helpers/permission_helper';

const customStyles = usecustomStyles();

const TEXT_CONSTANTS = {
    TITLE: '工地列表',
    CREATE: '新建'
};

const BaseTableColumns = [
    {
        title: '工地名称',
        dataIndex: 'siteName',
        sorter: (a, b) => a.siteName.localeCompare(b.siteName),
        sortDirections: ['ascend', 'descend'],
        align:'center'
    },
    {
        title: '所属项目',
        dataIndex: 'projectName',
        sorter: (a, b) => a.projectName.localeCompare(b.projectName),
        sortDirections: ['ascend', 'descend'],
        align:'center'
    },
    {
        title: '工地负责人',
        dataIndex: 'ownerName',
        sorter: (a, b) => a.ownerName.localeCompare(b.ownerName),
        sortDirections: ['ascend', 'descend'],
        align:'center'
    },
    {
        title: '联系方式',
        dataIndex: 'ownerPhone',
        sorter: (a, b) => a.ownerPhone.localeCompare(b.ownerPhone),
        sortDirections: ['ascend', 'descend'],
        align:'center'
    },
];

function DataList() {
    const selector = createSelector(
        state => state.Sites,
        state => state.Company,
        state => ({
            dataList: state.dataList,
            loading: state.loading
        })
    );

    const { dataList, loading } = useSelector(selector);
    const companyList = useSelector(state => state.Company.companyList);
    const projectLists = useSelector(state => state.Projects.projectList);
    const projectList = useSelector(state => state.Projects.dataList);
    
    const user = useSelector(state => state.Login.user);
    const companyId = user?.user?.companyId ? user?.user?.companyId : null;
    const projectId = user?.user?.projectId ? user?.user?.projectId : null;

    /**
     * 当前数据列表"加载中"状态
     */
    const [isLoading, setLoading] = useState(loading);

    /**
     * 当前页面的数据列表
     */
    const [localDataList, setLocalDataList] = useState([]);

    /**
     * 当前页面的数据列表的数据列
     */
    const [tableColumns, setTableColumns] = useState([]);

    /**
     * 显示删除"模态框"
     */
    const [deleteModalIsOpen, setDeleteModalIsOpen] = useState(false);

    /**
     * 显示编辑"模态框"
     */
    const [editModalIsOpen, setEditModalIsOpen] = useState(false);

    /**
     * 当前操作选中的数据行
     */
    const [selectedDataRow, setSelectedDataRow] = useState();

    /**
     * 编辑弹出框是"新建"模式还是"编辑"模式
     */
    const [isCreateModel, setIsCreateModel] = useState(false);

    /**
     * 显示编辑"新建"按钮
     */
    const [showCreateBtn, setShowCreateBtn] = useState(false);

    /**
     * 显示编辑"编辑"按钮
     */
    const [showEditBtn, setShowEditBtn] = useState(false);

    /**
     * 显示编辑"删除"按钮
     */
    const [showDeleteBtn, setShowDeleteBtn] = useState(false);

    const dispatch = useDispatch();
    const [form] = Form.useForm();

    // <--------------------- useEffect --------------------->
    useEffect(() => {
        dispatch(SThunk.getAll());
        if (!Array.isArray(projectList) || projectList.length === 0) {
            dispatch(PThunk.getAll()); 
        }
        if (!Array.isArray(companyList) || companyList.length === 0) {
            dispatch(CThunk.getCompany()); 
        }
    }, [dispatch]);

    useEffect(() => { 
        if (!Array.isArray(dataList)) {  
            return;
        }

        const userList = [];
        const projectMap = _.fromPairs((projectList || []).map(x => [x.id, x.projectName]));
        for (let i = 0; i < dataList.length; i++) {
            const item = { ...dataList[i] };
            item.roleName = USER_ROLE_VALUE_ENUM[item.role] || '';
            item.projectName = item.projectName || projectMap[item.projectId] || '-';
            userList.push(item);
        }

        setLocalDataList(userList);
    }, [dispatch, dataList, projectList]);

    useEffect(() => {
        form.setFieldsValue(selectedDataRow);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedDataRow]);

    useEffect(() => {
        const role = user?.user?.role;
        setShowCreateBtn(HPermission.hasPermission(role, HPermission.SITES_CREATE));
        setShowEditBtn(HPermission.hasPermission(role, HPermission.SITES_UPDATE));
        setShowDeleteBtn(HPermission.hasPermission(role, HPermission.SITES_DELETE));
    }, [user]);

    useEffect(() => {
        const columns = [...BaseTableColumns];
        if (showEditBtn || showDeleteBtn) {
            columns.push({
                title: ' ',
                render: item =>                 
                    (
                    <React.Fragment>
                        <Space wrap style={{ backgroundColor: themecolor.token.colorTextDisabled, padding: '11px' }} onClick={() => {setSelectedDataRow(item)}}>
                            {showEditBtn && (
                                <Button type="primary" ghost onClick={()=>onEditBtnClick(item.companyId)}>
                                    修改
                                </Button>
                            )}

                            {showDeleteBtn && (
                                <Button type="primary" danger ghost onClick={onDeleteBtnClick}>
                                    删除
                                </Button>
                            )}
                        </Space>
                    </React.Fragment>
                )
            });
        }
        setTableColumns(columns);
    }, [showEditBtn, showDeleteBtn]);

    // <--------------------- event handler --------------------->
    function onDeleteBtnClick() {
        setDeleteModalIsOpen(true);
    }

    const onCreateBtnClick = () => {
        setEditModalIsOpen(true);
        setIsCreateModel(true);
    };

    const onEditBtnClick = (id) => {
        dispatch(PThunk.getProject(id));
        setEditModalIsOpen(true);
        setIsCreateModel(false);
    };

    const onEditModalClose = () => {
        setEditModalIsOpen(false);
        form.resetFields();
    };

    const onDeleteCanceled = () => {
        setDeleteModalIsOpen(false);
        setSelectedDataRow(null);
    };

    const onDeleteConfirmed = () => {
        if (selectedDataRow) {
            dispatch(SThunk.remove(selectedDataRow.id));
        }

        setDeleteModalIsOpen(false);
        setSelectedDataRow(null);
    };

    async function onSubmitBtnClick(values) {
        if (isCreateModel) {
            dispatch(SThunk.create(values));
        } else {
            values.id = selectedDataRow.id;
            dispatch(SThunk.update(values));
        }
        onEditModalClose();
    }

    function getProject(value) {
        if (value) {   
            form.resetFields(['projectId'])
            dispatch(PThunk.getProject(value));
        }
    }

    // <--------------------- other configs --------------------->
    const pagination = {
        pageSizeOptions: ['5', '10', '20', '50'], // 页条数下拉选择器的选项
        defaultPageSize: 5, // 默认的页大小
        showSizeChanger: true, // 显示页大小改变器
    };

    const SelectStyle = { width: '100%', padding: '0px', height: 35 };

    return (
        <React.Fragment>
            {/* Delete */}
            <DeleteModal show={deleteModalIsOpen} handleClose={onDeleteCanceled} deleteModalFunction={onDeleteConfirmed} />

            {/* Api */}
            <Card style={{ marginTop: customStyles.margin, marginBottom: customStyles.margin }} className="ApiTable">
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: customStyles.margin }}>
                    <h4 style={{ fontSize: '16px', fontWeight: '500' }}>{TEXT_CONSTANTS.TITLE}</h4>
                    <div style={{ display: 'flex', flex: 'wrap' }}>
                        {showCreateBtn && (
                            <Button type="primary" style={{ backgroundColor: customStyles.colorSuccess, boxShadow: 'none' }} onClick={onCreateBtnClick}>
                                <PlusOutlined />
                                {TEXT_CONSTANTS.CREATE}
                            </Button>
                        )}
                    </div>
                </div>
                {isLoading ? (
                    <Spinners setLoading={setLoading} />
                ) : (
                    <React.Fragment>
                        {localDataList && localDataList.length > 0 ? (
                            <div style={{ overflowX: 'auto', whiteSpace: 'nowrap' }}>
                                <Table className='table-list' rowKey="id" columns={tableColumns} dataSource={localDataList} pagination={pagination} scroll={{y:'calc(100vh - 408px)'}}/>
                            </div>
                        ) : (
                            <Empty image="/nodata.png" description={<span></span>} style={{ padding: '10%' }}></Empty>
                        )}
                    </React.Fragment>
                )}
            </Card>

            <Modal centered title={isCreateModel ? '创建工地数据' : '修改工地数据'} open={editModalIsOpen} onCancel={onEditModalClose} footer={[]}>
                <div>
                    <Form form={form} onFinish={onSubmitBtnClick}>
                        <div>
                            <div className="siteName">
                                <label style={{ marginBottom: '10px', display: 'block', fontWeight: 500 }}> 工地名称 </label>
                                <Form.Item name="siteName" rules={[{ required: true, message: '工地名称不能为空' }]}>
                                    <Input name="siteName" type="text" />
                                </Form.Item>
                            </div>

                            <div className="companyId" style={{ marginTop: customStyles.marginSM }}>
                                <label style={{ marginBottom: '10px', display: 'block', fontWeight: 500 }}> 所属公司 </label>
                                <Form.Item name="companyId" initialValue={companyId} rules={[{ required: true, message: '所属公司为必选项' }]}>
                                    <Select name="companyId" defaultValue={companyId} disabled={companyId !== null} onChange={getProject} options={(companyList || []).map(x => ({ label: x.companyName, value: x.id }))} style={SelectStyle} />
                                </Form.Item>
                            </div>

                            <div className="projectId" style={{ marginTop: customStyles.marginSM }}>
                                <label style={{ marginBottom: '10px', display: 'block', fontWeight: 500 }}> 所属项目 </label>
                                <Form.Item name="projectId" initialValue={projectId} rules={[{ required: true, message: '所属项目为必选项' }]}>
                                    <Select name="projectId"  defaultValue={projectId} disabled={projectId !== null} options={(projectLists || []).map(x => ({ label: x.projectName, value: x.id }))} style={SelectStyle} />
                                </Form.Item>
                            </div>

                            <div className="ownerName" style={{ marginTop: customStyles.marginSM }}>
                                <label style={{ marginBottom: '10px', display: 'block', fontWeight: 500 }}> 工地负责人 </label>
                                <Form.Item name="ownerName" rules={[{ required: true, message: '工地负责人信息不能为空' }]}>
                                    <Input name="ownerName" type="text" />
                                </Form.Item>
                            </div>

                            <div className="ownerPhone" style={{ marginTop: customStyles.marginSM }}>
                                <label style={{ marginBottom: '10px', display: 'block', fontWeight: 500 }}> 联系方式 </label>
                                <Form.Item name="ownerPhone">
                                    <Input name="ownerPhone" type="tel" pattern="+([0-9]{3}) [0-9]{2} [0-9]{3} [0-9]{4}" />
                                </Form.Item>
                            </div>
                        </div>

                        <div style={{ textAlign: 'end' }}>
                            <Button htmlType="submit" type="primary">
                                确定
                            </Button>
                        </div>
                    </Form>
                </div>
            </Modal>
        </React.Fragment>
    );
}

export default DataList;
